"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Wrench, RefreshCw, CircleCheck as CheckCircle2, Phone, ShieldCheck, CircleAlert as AlertCircle, CircleQuestionMark as HelpCircle, Clock } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const repairFactors = [{
  id: 1,
  title: "Unit is under 8-10 years old",
  detail: "Standard tank water heaters typically have a lifespan of 8 to 12 years with routine maintenance."
}, {
  id: 2,
  title: "Isolated component malfunction",
  detail: "Issues like a failed heating element, faulty thermostat, or broken pressure valve are fast, low-cost fixes."
}, {
  id: 3,
  title: "Repair is under 50% of replacement cost",
  detail: "If the one-time repair estimate is less than half the price of a new unit, repair is usually the practical choice."
}, {
  id: 4,
  title: "First major breakdown in years",
  detail: "A single unexpected failure on an otherwise well-maintained water heater rarely warrants total replacement."
}];
const replaceFactors = [{
  id: 1,
  title: "Tank body is actively leaking",
  detail: "An internal tank breach or pooling water at the bottom cannot be patched and requires immediate replacement."
}, {
  id: 2,
  title: "System is over 10-12 years old",
  detail: "Aging systems become inefficient, suffer recurring component failures, and pose higher flood risks."
}, {
  id: 3,
  title: "Frequent, compounding repair bills",
  detail: "If you have called for service multiple times in the last 18 months, replacing provides immediate reliability."
}, {
  id: 4,
  title: "Persistent rust or discolored hot water",
  detail: "Rust in hot water indicates interior tank corrosion, signaling imminent catastrophic failure."
}];
export default function RepairReplace() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="repair-or-replace" data-nav="light" className="bg-background py-14 md:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-[1240px] px-5 md:px-10">
        
        {/* Section Header */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.2
      }} className="max-w-3xl mb-12 md:mb-16">
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-bold uppercase tracking-wider mb-4 border border-border">
            <HelpCircle className="size-3.5 text-primary" />
            <span>Water Heater Decision Guide</span>
          </motion.div>
          
          <motion.h2 variants={itemVariants} className="font-default text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground tracking-tight leading-[1.15] mb-4">
            Repair or Replace? How to Make the Right Call.
          </motion.h2>
          
          <motion.p variants={itemVariants} className="text-muted-foreground text-base md:text-lg leading-relaxed">
            When your hot water stops, you need clear facts, not high-pressure sales. Use this straightforward checklist to evaluate whether a fast repair or a new energy-efficient replacement makes financial sense for your Jonesboro home.
          </motion.p>
        </motion.div>

        {/* Main Grid: Comparison Cards + Supporting Media */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Comparison Cards Column (7 Columns) */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            
            {/* Repair Option Card */}
            <motion.div initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.35,
            ease: "easeOut"
          }}>
              <Card className="border-border bg-card shadow-sm hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="size-11 rounded-lg bg-secondary flex items-center justify-center text-primary shrink-0 border border-border">
                        <Wrench className="size-5" />
                      </div>
                      <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Option A</span>
                        <CardTitle className="text-xl md:text-2xl font-bold text-foreground">
                          When to Repair
                        </CardTitle>
                      </div>
                    </div>
                    <span className="hidden sm:inline-block px-3 py-1 rounded-full bg-secondary text-primary font-semibold text-xs border border-border">
                      Budget-Friendly
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <p className="text-sm text-muted-foreground mb-4 font-medium">
                    Repair is usually the fastest, most cost-effective path if your heater meets these conditions:
                  </p>
                  <div className="grid grid-cols-1 gap-3.5">
                    {repairFactors.map((factor, i) => <div key={factor.id} data-index={i} className="flex items-start gap-3 p-3 rounded-lg bg-background border border-border">
                        <CheckCircle2 className="size-4 text-chart-4 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-foreground leading-tight mb-0.5">
                            {factor.title}
                          </p>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {factor.detail}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Replace Option Card */}
            <motion.div initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.35,
            delay: shouldReduceMotion ? 0 : 0.08,
            ease: "easeOut"
          }}>
              <Card className="border-border bg-card shadow-sm border-l-4 border-l-accent hover:border-primary/40 transition-[border-color,box-shadow] duration-200">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="size-11 rounded-lg bg-secondary flex items-center justify-center text-primary shrink-0 border border-border">
                        <RefreshCw className="size-5" />
                      </div>
                      <div>
                        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Option B</span>
                        <CardTitle className="text-xl md:text-2xl font-bold text-foreground">
                          When to Replace
                        </CardTitle>
                      </div>
                    </div>
                    <span className="hidden sm:inline-block px-3 py-1 rounded-full bg-accent/20 text-accent-foreground font-semibold text-xs border border-accent/30">
                      Long-Term Value
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="pt-2">
                  <p className="text-sm text-muted-foreground mb-4 font-medium">
                    Replacing your unit prevents severe property damage and saves on monthly utility bills when:
                  </p>
                  <div className="grid grid-cols-1 gap-3.5">
                    {replaceFactors.map((factor, i) => <div key={factor.id} data-index={i} className="flex items-start gap-3 p-3 rounded-lg bg-background border border-border">
                        <AlertCircle className="size-4 text-destructive shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm font-semibold text-foreground leading-tight mb-0.5">
                            {factor.title}
                          </p>
                          <p className="text-xs text-muted-foreground leading-relaxed">
                            {factor.detail}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

          </div>

          {/* Supporting Image & Diagnostic Trust Column (5 Columns) */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            
            {/* Technician Image Card */}
            <motion.div initial={{
            opacity: 0,
            scale: shouldReduceMotion ? 1 : 0.98
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.4,
            ease: "easeOut"
          }} className="relative rounded-2xl overflow-hidden border border-border bg-card shadow-md">
              <div className="aspect-[4/3] sm:aspect-[16/11] lg:aspect-[4/3] relative w-full overflow-hidden">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_technician_inspection.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_technician_inspection.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1778865/img/water_heater_technician_inspection.png-wvc-sizes" alt="Jonesboro licensed plumber inspecting water heater valves and electrical elements" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent" />
                
                {/* Floating On-Site Badge */}
                <div className="absolute bottom-4 left-4 right-4 text-primary-foreground">
                  <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck className="size-4 text-accent shrink-0" />
                    <span className="text-xs font-bold uppercase tracking-wider">Jonesboro Local Team</span>
                  </div>
                  <p className="text-sm font-semibold leading-tight">
                    Upfront diagnostic assessments with zero hidden fees.
                  </p>
                </div>
              </div>

              {/* Quick Insight Panel below image */}
              <div className="p-6 bg-card">
                <div className="flex items-start gap-4 mb-5">
                  <div className="size-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center font-bold text-sm shrink-0">
                    50%
                  </div>
                  <div>
                    <h3 className="font-default text-base font-bold text-card-foreground mb-1">
                      The Plumber's 50% Rule
                    </h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      If a repair costs more than 50% of the replacement value and the unit is over halfway through its expected lifespan, replacement offers better return on investment.
                    </p>
                  </div>
                </div>

                <div className="pt-4 border-t border-border flex flex-col gap-3">
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5 font-medium">
                      <Clock className="size-3.5 text-primary" />
                      24/7 Rapid Diagnostics
                    </span>
                    <span className="font-semibold text-foreground">Jonesboro, GA</span>
                  </div>
                  
                  {/* Call Direct Action */}
                  <div className="pt-2">
                    <a href="tel:4708479076" className="block w-full focus:outline-none">
                      <Button size="lg" className="w-full rounded-full bg-accent text-accent-foreground font-bold hover:brightness-105 shadow-md transition-all duration-150 flex items-center justify-center gap-2.5">
                        <Phone className="size-4 fill-current" />
                        <span>Call (470) 847-9076 for Advice</span>
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Quick Consultation Prompt */}
            <motion.div initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.35,
            delay: shouldReduceMotion ? 0 : 0.1,
            ease: "easeOut"
          }} className="p-5 rounded-xl bg-secondary/50 border border-border flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-bold text-foreground">Unsure what you need?</p>
                <p className="text-xs text-muted-foreground">Our local technician will inspect it and walk you through every option.</p>
              </div>
              <Link to="/contact/" className="shrink-0">
                <Button variant="outline" size="sm" className="rounded-full bg-background border-border text-foreground hover:bg-muted font-semibold text-xs">
                  Book Visit
                </Button>
              </Link>
            </motion.div>

          </div>

        </div>

      </div>
    </section>;
}